import { useState, useCallback } from 'react';
import { toast } from 'sonner';

/**
 * Hook personnalisé pour gérer l'état de soumission de formulaire
 * Fournit un état de chargement et une fonction wrapper pour les soumissions async
 */
export const useSubmitState = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);

  /**
   * Wrap une fonction asynchrone avec gestion d'état et d'erreurs
   * @param {Function} asyncFn - Fonction asynchrone à exécuter
   * @param {Object} options - Options de configuration
   * @returns {Promise} Résultat de la fonction
   */
  const withSubmit = useCallback(async (asyncFn, options = {}) => {
    const {
      onSuccess,
      onError,
      successMessage,
      errorMessage = "Une erreur est survenue",
      preventDoubleSubmit = true
    } = options;

    // Prévenir les doubles soumissions
    if (preventDoubleSubmit && isSubmitting) {
      return;
    }

    setIsSubmitting(true);

    try {
      const result = await asyncFn();
      
      // Afficher message de succès
      if (successMessage) {
        toast.success(successMessage);
      }
      
      // Callback de succès
      if (onSuccess) {
        onSuccess(result);
      }
      
      return result;
    } catch (error) {
      console.error('Submit error:', error);
      
      // Extraire le message d'erreur de l'API
      let displayMessage = errorMessage;
      let fieldErrors = {};
      
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        
        // Si detail est un objet structuré (notre nouveau format)
        if (typeof detail === 'object' && detail.message) {
          displayMessage = detail.message;
          
          // Si un champ spécifique est en erreur
          if (detail.field) {
            fieldErrors[detail.field] = detail.message;
          }
        } 
        // Si detail est une string simple (ancien format)
        else if (typeof detail === 'string') {
          displayMessage = detail;
        }
      }
      
      // Afficher le toast d'erreur
      toast.error(displayMessage);
      
      // Callback d'erreur avec les détails
      if (onError) {
        onError(error, fieldErrors);
      }
      
      throw error;
    } finally {
      setIsSubmitting(false);
    }
  }, [isSubmitting]);

  return {
    isSubmitting,
    setIsSubmitting,
    withSubmit
  };
};

/**
 * Formater un message d'erreur API
 * @param {Error} error - L'erreur capturée
 * @param {string} defaultMessage - Message par défaut
 * @returns {Object} { message: string, fieldErrors: Object }
 */
export const formatAPIError = (error, defaultMessage = "Une erreur est survenue") => {
  let message = defaultMessage;
  let fieldErrors = {};
  let errorCode = null;

  if (error.response?.data?.detail) {
    const detail = error.response.data.detail;
    
    if (typeof detail === 'object') {
      message = detail.message || defaultMessage;
      errorCode = detail.code;
      
      if (detail.field) {
        fieldErrors[detail.field] = detail.message;
      }
    } else if (typeof detail === 'string') {
      message = detail;
    }
  }

  return { message, fieldErrors, errorCode };
};
